import {
  MultiSelectDropdown,
  SingleSelectDropdown,
  type FilterGroup,
  type FilterSelection,
  type MultiSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryDropdownModes } from "@/app/theme";
import { lockExclusiveTagChoices, normalizeTagSelection } from "@/components/label";
import { isPresetActive, type DatasetFilterPreset } from "./datasetFilters";
import type { DatasetFilterController } from "./DatasetFilterController";

// 프리셋 토글이다. 활성이면 프리셋 값을 선택에서 뺀다(다른 값은 유지). 비활성이면 해당 key 를
// 프리셋 값으로 설정한다(교체). 레일(DatasetFilter)과 고급 필터 시트가 함께 쓴다.
export function applyPreset(
  controller: DatasetFilterController,
  selection: FilterSelection,
  preset: DatasetFilterPreset,
): void {
  const active = isPresetActive(preset, selection);
  for (const [key, values] of Object.entries(preset.selection)) {
    if (active) {
      const current = selection[key] ?? [];
      controller.filterController.setGroupSelection(
        key,
        current.filter((value) => !values.includes(value)),
      );
    } else {
      controller.filterController.setGroupSelection(key, [...values]);
    }
  }
}

// 드롭다운 facet 에 검색 입력을 켜는 옵션 수 하한이다. 눈으로 보고 고를 수 있는 규모(source 4종 등)는
// 검색 없이 둔다. task·embodiment 처럼 목록이 긴 축만 한 번에 켠다. facet 마다 따로 정하지 않고
// 임계값 하나로 UX 를 통일한다.
const SEARCHABLE_OPTION_THRESHOLD = 8;

export type FacetControlProps = {
  controller: DatasetFilterController;
  group: FilterGroup;
  selectedValues: readonly string[];
  disabled: boolean;
  fullWidth?: boolean;
};

// facet 한 개를 다중선택 MultiSelectDropdown 으로 렌더한다. 선택은 공유 FilterController 가
// 소유한다. 레일(DatasetFilter)과 고급 필터 시트(DatasetFilterSheet)가 함께 쓴다.
export function FacetControl({
  controller,
  group,
  selectedValues,
  disabled,
  fullWidth,
}: FacetControlProps): ReactNode {
  // 에피소드 결함에서 존재 토큰(결함 있음·없음)과 값·갈래는 함께 쓸 수 없다. 잠금은 선택에 따라
  // 바뀌는 표시 규칙이다. 그래서 카탈로그가 아니라 여기서 추가한다. 카탈로그 옵션에 disabled 를
  // 넣으면 c6 정규화가 이미 적용된 값을 경고 없이 버린다.
  const options =
    group.value === "episode_quality"
      ? lockExclusiveTagChoices(group.filters, selectedValues)
      : group.filters;
  if (group.display === "select") {
    return (
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.filter}
        size="sm"
        fullWidth={fullWidth}
        label={group.label}
        options={[{ value: "", label: "전체" }, ...group.filters]}
        value={selectedValues[0] ?? ""}
        neutralValue=""
        disabled={disabled || group.disabled}
        onValueChange={(value) =>
          controller.filterController.setGroupSelection(group.value, value ? [value] : [])
        }
      />
    );
  }
  return (
    <MultiSelectDropdown
      mode={hatcheryDropdownModes.filter}
      size="sm"
      fullWidth={fullWidth}
      label={group.label}
      options={options as readonly MultiSelectDropdownOption[]}
      menuWidth={group.value === "episode_quality" ? "26rem" : undefined}
      maxMenuHeight={group.value === "episode_quality" ? "min(70vh, 32rem)" : undefined}
      selectedValues={selectedValues}
      disabled={disabled || group.disabled}
      closeOnSelect={false}
      searchable={group.filters.length >= SEARCHABLE_OPTION_THRESHOLD}
      searchPlaceholder="검색"
      clearSelectionLabel="선택 해제"
      onSelectionChange={(values) =>
        controller.filterController.setGroupSelection(
          group.value,
          group.value === "episode_quality" ? normalizeTagSelection(group.filters, values) : values,
        )
      }
    />
  );
}
